import { decodeGsxMeta } from "../format/decoder";
import type { GsxMeta } from "../format/format";
import type { GsplatTransport } from "./types";

export interface ServerTransportOptions {
  /** Direct URL to the .gsx file, e.g. "https://host/project_{id}/AR/file.gsx". */
  fileUrl: string;
  /** Job id (used only for error messages / logging). */
  jobId: string;
  /** Returns an Authorization header value (e.g. "Bearer ey..."). Re-evaluated per request. */
  authHeader?: () => string;
  /** When true, send fetch with `credentials: "include"` for cookie-based session auth. */
  withCredentials?: boolean;
}

/**
 * Default GsplatTransport for sc-quick3d's REST API.
 *
 * Route used:
 *   GET /api/v1/convert/gsplat/by-project/:projectId
 *     Returns { jobId, outputKey, ... }. The caller constructs the file URL as
 *     `${serverHost}/${outputKey}` and passes it as `fileUrl`.
 *
 * To consume:
 *   const t = createServerTransport({ fileUrl: `${host}/${outputKey}`, jobId, withCredentials: true });
 *   attachGsplatLayer({ jobId, transport: t, renderer, scene, camera, ... });
 */
export function createServerTransport(opts: ServerTransportOptions): GsplatTransport {
  console.log(`[gsplat] transport: file URL = ${opts.fileUrl}`);

  let metaPromise: Promise<GsxMeta> | null = null;

  const getMeta = (): Promise<GsxMeta> => {
    if (!metaPromise) {
      metaPromise = fetchMeta();
    }
    return metaPromise;
  };

  async function fetchMeta(): Promise<GsxMeta> {
    const HEADER_SIZE = 96;
    const ENTRY_SIZE = 48;
    const NODE_SIZE = 72;
    console.log("[gsplat] transport: fetching header (first 96 bytes)");
    const headRes = await rangeFetch(0, HEADER_SIZE - 1);
    const headBytes = new Uint8Array(await headRes.arrayBuffer());
    const view = new DataView(headBytes.buffer);
    const blockCount = view.getUint32(12, true);
    const nodeCount = view.getUint32(52, true);
    const tablesEnd = HEADER_SIZE + blockCount * ENTRY_SIZE + nodeCount * NODE_SIZE - 1;
    console.log(
      `[gsplat] transport: header parsed blockCount=${blockCount} nodeCount=${nodeCount}, fetching tables ${HEADER_SIZE}..${tablesEnd}`,
    );
    const tablesRes = await rangeFetch(HEADER_SIZE, tablesEnd);
    const tablesBytes = new Uint8Array(await tablesRes.arrayBuffer());
    const combined = new Uint8Array(headBytes.byteLength + tablesBytes.byteLength);
    combined.set(headBytes, 0);
    combined.set(tablesBytes, headBytes.byteLength);
    console.log(
      `[gsplat] transport: meta tables fetched (${combined.byteLength} bytes)`,
    );
    return decodeGsxMeta(combined);
  }

  async function rangeFetch(start: number, end: number): Promise<Response> {
    const headers: Record<string, string> = { Range: `bytes=${start}-${end}` };
    if (opts.authHeader) headers.Authorization = opts.authHeader();
    const res = await fetch(opts.fileUrl, {
      method: "GET",
      headers,
      credentials: opts.withCredentials ? "include" : "same-origin",
    });
    if (!res.ok && res.status !== 206) {
      const text = await res.text().catch(() => "");
      throw new Error(`Range GET → ${res.status} ${res.statusText}: ${text}`);
    }
    return res;
  }

  const getBlockBytes = async (
    _blockId: number,
    hint?: { offset: number; sizeCompressed: number },
  ): Promise<Uint8Array> => {
    if (!hint) {
      throw new Error("server transport requires an offset/sizeCompressed hint");
    }
    const start = hint.offset;
    const end = hint.offset + hint.sizeCompressed - 1;
    const res = await rangeFetch(start, end);
    return new Uint8Array(await res.arrayBuffer());
  };

  return { getMeta, getBlockBytes };
}
